<template>
  <RouterLink
    :to="getPbSheetLink(problem.id)"
    :event="noRouteLink ? '' : 'click'"
    class="problem-item generic-border generic-border--clickable generic-border--card"
    :class="elClasses"
  >
    <div
      ref="tile"
      class="f-w v-centerer"
      @click="itemClicked"
    >
      <div class="problem-item__image h-v-centerer">
        <Icon
          v-if="!fUtilsContainsPictures && problem.files && problem.files.length"
          :color="fUtilsFirstFileIcon.color"
        >
          {{ fUtilsFirstFileIcon.icon }}
        </Icon>
        <img v-else :src="fUtilsFirstPictureData" alt="">
        <div class="problem-item__id">{{ problem.id }}</div>
      </div>

      <div class="problem-item__description">
        <EllipsisDiv
          :key="whatKey"
          class="notranslate problem-item__description__what"
          :text="safeAutocompleteString(problem.lWhat)"
        />
        <ImpactIndicator :value="Number(problem.impact)" class="problem-item__description__impact" />
      </div>

      <div class="problem-item__tags h-v-centerer">
        <Icon v-if="problem.accessGranted === 1" v-quick-title="$t('message.PB_HELP_ACCESS_GRANTED')">
          {{ share }}
        </Icon>
        <Icon v-if="problem.gwStep > 0" v-quick-title="$t('message.PROBLEM_FROM_GW')">
          {{ hardhatExtraBold }}
        </Icon>
        <Icon v-if="problem.secuState > 1" v-quick-title="securingStateHint" :class="securingIconClass(problem)">
          {{ securing }}
        </Icon>
        <div
          v-if="wasReportedInTheLast24h"
          v-quick-title="$t('message.PB_BEEN_REPORTED_TODAY')"
          class="generic-border--tag generic-border--blue-tag generic-border--disabled"
        >
          {{ $t('message.TWENTY_FOUR_HOURS') }}
        </div>
      </div>


      <div class="problem-item__leader h-v-centerer">
        <UserThumbnail
          :value="problem.leader || 0"
          fetch-data
          :show-name="problem.leader > 0 && !elClasses['tablet-size']"
          wrap
          disabled
        />
      </div>

      <div
        class="problem-item__deadline h-v-centerer"
        :class="{ 'problem-item__deadline--disabled': !problem.deadline }"
      >
        <span class="problem-item__deadline__date">
          {{ displayedDeadline }}
          <div v-if="problemIsLate" class="problem-item__deadline__date__late" />
        </span>
      </div>

      <div class="problem-item__impact h-v-centerer">
        <ImpactIndicator :value="Number(problem.impact)" :min="!!elClasses['sm-landscape-size']" />
      </div>

      <div v-if="problem.state !== undefined" class="problem-item__state v-centerer" :style="stateStyle">
        <span class="dot">●</span>
        <span>{{ stateTitle }}</span>
      </div>

      <div v-if="!noMenu" class="problem-item__archive h-v-centerer">
        <div
          v-if="problem.archived === undefined || Number(problem.archived) === 0"
          class="generic-border generic-border--icon-button generic-border--clickable generic-border--gray-border"
          @click.prevent="remove(true)"
        >
          <Icon>{{ archivesBox }}</Icon>
        </div>
        <div
          v-else
          class="generic-border generic-border--icon-button generic-border--clickable generic-border--gray-border"
          @click.prevent="restore()"
        >
          <Icon>{{ cancelMedium }}</Icon>
        </div>
      </div>

      <div v-if="!noMenu && !noDeleteBtn" class="problem-item__delete h-v-centerer">
        <div
          class="generic-border generic-border--icon-button generic-border--clickable generic-border--gray-border"
          @click.prevent="remove(false)"
        >
          <Icon>{{ deleteMedium }}</Icon>
        </div>
      </div>
    </div>
  </RouterLink>
</template>

<script>
/**
 * Item in a problems list
 *
 * @vue-prop {Object} problem - Related problem
 * @vue-prop {Boolean} [noDeleteBtn=false] - If true, remove the delete button
 * @vue-prop {Boolean} [noMenu=false] - If true, remove the delete and archive buttons
 * @vue-prop {Boolean} [noRouteLink=false] - If true, clicking the item triggers a
 *                                           'click' event instead of a route change
 *
 */
import $ from 'jquery';
// Vuex utils
import { DELETE_PROBLEM, ARCHIVE_PROBLEM, RESTORE_PROBLEM } from '@/store/modules/problem/action-types';
// Components
import EllipsisDiv from '@/components/ellipsize.vue';
import Icon from '@/components/icon.vue';
import ImpactIndicator from '@/components/impact-indicator.vue';
import UserThumbnail from '@/components/user-thumbnail.vue';
// Mixins
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import mixinFileUtils from '@/mixins/file-utils.vue';
// Utils
import {
  archivesBox,
  cancelMedium,
  deleteMedium,
  hardhatExtraBold,
  securing,
  share,
} from '@/assets/icons';
import { SColors, PB_STATE_COLORS } from '@/utils/constants';
import { safeAutocompleteString, calert, itemWidthClasses } from '@/utils/main';
import { securingIconClass } from '@/utils/problem-utils';

export default {

  name: 'ProblemItem',

  components: {
    EllipsisDiv,
    Icon,
    ImpactIndicator,
    UserThumbnail,
  },

  mixins: [mixinSocketHandler, mixinFileUtils],

  props: {
    problem: {
      type: Object,
      required: true,
    },
    noDeleteBtn: Boolean,
    noMenu: Boolean,
    noRouteLink: Boolean,
  },

  data() {
    return {
      archivesBox,
      cancelMedium,
      deleteMedium,
      hardhatExtraBold,
      securing,
      share,
      SColors,
      elClasses: '',
      whatKey: 1,
    };
  },

  computed: {
    /** String: Text displayed as the state's description */
    stateTitle() {
      return this.$t('message.PB_STATES')[this.problem.state - 1];
    },
    /** Object: CSS style of the state div */
    stateStyle() {
      return { color: PB_STATE_COLORS[this.problem.state] };
    },
    /** Boolean: True if the problem has been reported in the last 24 hours */
    wasReportedInTheLast24h() {
      const diff = this.$moment().diff(this.problem.created, 'hours');
      return diff <= 24 && diff >= 0;
    },
    /** String: Text to display for the deadline column */
    displayedDeadline() {
      const dFormat = this.elClasses['sm-landscape-size'] ? this.$t('yyDateFormat') : this.$t('fullDateFormat');
      return this.problem.deadline
        ? this.$moment(this.problem.deadline).format(dFormat)
        : this.$t('message.TO_BE_DEFINED');
    },
    /** Boolean: true if the problem is late */
    problemIsLate() {
      return this.problem.state < 4 && this.$moment(this.problem.deadline).isBefore(this.$moment());
    },
    securingStateHint() {
      switch (this.problem.secuState) {
        case 2: return this.$t('message.SECU_STATE_PENDING');
        case 3: return this.$t('message.SECU_STATE_ONGOING');
        case 4: return this.$t('message.SECU_STATE_COMPLETED');
        default: return '';
      }
    },
    /** Problem from vuex state */
    stateProblem() {
      return this.$store.state.problem.problems[`${this.problem.id}`];
    },
    fUtilsFileIds() {
      return this.problem.files || [];
    },
  },

  created() {
    $(window).on(`resize.${this.uuid}`, this.onSizeChanged);
  },

  mounted() {
    this.onSizeChanged();
  },

  beforeDestroy() {
    $(window).off(`resize.${this.uuid}`);
  },

  methods: {
    securingIconClass,
    safeAutocompleteString,
    /** Respond to click event if noRouteLink is true */
    itemClicked(e) {
      if (this.noRouteLink) {
        this.$emit('click', e);
      }
    },
    remove(archive) {
      calert(archive ? this.$t('message.CONFIRM_ARCHIVE_PB') : this.$t('message.CONFIRM_DELETE_PB'), [
        {
          title: archive ? this.$t('message.ARCHIVE') : this.$t('message.DELETE'),
          action: () => {
            this.confirmRemove(archive);
          },
          sClass: 'red',
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    confirmRemove(archive) {
      const action = archive ? ARCHIVE_PROBLEM : DELETE_PROBLEM;
      this.$store.dispatch(`problem/${action}`, { id: this.problem.id }).then(() => {
        this.$emit('removed');
      });
    },
    /* Restore previously archived problem */
    restore() {
      this.$store.dispatch(`problem/${RESTORE_PROBLEM}`, { id: this.problem.id }).then(() => {
        this.$emit('removed');
      });
    },
    /** Style of the problem is determined by the size of $el. Apply classes */
    onSizeChanged() {
      const width = this.$refs.tile ? this.$refs.tile.offsetWidth : 0;
      this.elClasses = itemWidthClasses
        .reduce((classes, wc) => ({ ...classes, [wc.className]: width <= wc.width }), {});
      this.$nextTick(() => {
        // Change the key after the withe-space property has been updated
        this.whatKey++;
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.problem-item {
  margin-top: 0.6rem;
  cursor: pointer;
  position: relative;
  height: $main-tile-height;
  font-size: 0.86rem;
  display: block;

  &__image {
    width: calc(#{$problem-item-height} - calc(2 * #{$generic-border-width}));
    height: calc(#{$problem-item-height} - calc(2 * #{$generic-border-width}));
    flex: 0 0 $problem-item-height;
    padding: 0.6rem;
    box-sizing: border-box;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      border-radius: $main-border-radius;
    }
  }

  &__id {
    position: absolute;
    bottom: 0;
    left: 0;
    background-color: rgba(255, 255, 255, 0.75);
    font-size: 0.8rem;
    font-weight: 700;
    border-radius: $small-border-radius;
    padding: 0 0.1rem;
    width: 100%;
    text-align: center;
    box-sizing: border-box;
  }

  &__description {
    flex: 1;
    min-width: 0px;
    margin-left: 0.7rem;
    line-height: 1.3rem;
    height: 2.6rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    font-size: 1rem;

    .ellipsis-div {
      height: 100%;
    }

    & &__impact {
      display: none;
    }
  }

  &__leader {
    min-width: 0px;
    flex: 0 0 7.5rem;

    .user-thumbnail {
      width: 100%;

      .editable-label {
        width: 100%;

        .editable-label-text > div {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
      }
    }
  }

  &__deadline {
    flex: 0 0 10rem;
    font-size: 1rem;

    &--disabled {
      opacity: 0.7;
    }

    &__date {
      position: relative;

      &__late {
        position: absolute;
        width: .5rem;
        height: .5rem;
        background-color: $color-shizen-red;
        border-radius: 1em;
        right: -1em;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }

  &__impact {
    flex: 0 0 12rem;
  }

  &__state {
    flex: 0 0 12rem;

    .dot {
      margin-right: .4rem;
    }
  }

  &__tags {
    flex: 0 0 5.86rem;
    flex-wrap: wrap;

    .icon, img {
      width: 1rem;
      height: 1rem;
      margin: 0.3rem 0.2rem;
      padding: 0.35rem;
      background: $color-gray-background;
      border-radius: $small-border-radius;
    }
  }

  &__archive {
    flex: 0 0 5.72rem;
  }

  &__delete {
    flex: 0 0 5.72rem;
  }

  .securing-state {
    width: 20px;

    img {
      width: 20px;
      height: auto;
    }
  }

  &.tablet-size {
    .problem-item {
      &__leader {
        flex: 0 0 4rem;
      }
      &__deadline {
        flex: 0 0 6.5rem;
      }
      &__impact {
        flex: 0 0 8.5rem;
      }
      &__state {
        flex: 0 0 11.5rem;
      }
      &__tags {
        flex: 0 0 4.86rem;
      }
      &__archive {
        flex: 0 0 4.72rem;
      }
      &__delete {
        flex: 0 0 4.72rem;
      }
    }
  }

  &.sm-landscape-size {
    .problem-item {
      &__leader {
        flex: 0 0 3rem;
      }
      &__deadline {
        flex: 0 0 6rem;
      }
      &__impact {
        flex: 0 0 4rem;
        margin-right: .3rem;
      }
      &__state {
        display: none;
      }
      &__tags {
        display: none;
      }
      &__archive {
        flex: 0 0 3.72rem;
      }
      &__delete {
        flex: 0 0 3.72rem;
      }
    }
  }

  &.sm-portrait-size {
    .problem-item {
      &__image {
        width: 3rem;
        flex: 0 0 3rem;
      }
      &__description {
        margin-left: 0;
      }
      &__impact {
        flex: 0 0 4rem;
        margin-right: .3rem;
      }
      &__deadline, &__leader {
        display: none;
      }
    }
  }

  &.sm-small-portrait-size {
    .problem-item {
      &__image {
        width: calc(#{$problem-item-height} - calc(2 * #{$generic-border-width}));
        flex: 0 0 calc(#{$problem-item-height} - calc(2 * #{$generic-border-width}));
      }
      &__description {
        display: block;
        height: auto;

        &__what {
          height: 1.3rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ellipsis-size-monitor {
          overflow: hidden;
        }

        &__impact {
          display: flex;
          margin-top: .2rem;

          &.impact-indicator {
            height: 1.5rem;
            display: inline-flex;
          }
        }
      }
      &__impact {
        display: none;
      }
    }
  }
}

</style>
